import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { allArticles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = allArticles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="font-heading font-bold text-2xl text-foreground-900">分类未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "@id": "https://51chigua.com/category/" + category.slug + "/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首页",
            "item": "https://51chigua.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "分类",
            "item": "https://51chigua.com/category"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": category.name
          }
        ]
      },
      {
        "@type": "CollectionPage",
        "@id": "https://51chigua.com/category/" + category.slug + "/#webpage",
        "url": "https://51chigua.com/category/" + category.slug,
        "name": category.name + " - 51吃瓜App",
        "description": category.description,
        "isPartOf": {
          "@type": "WebSite",
          "@id": "https://51chigua.com/#website"
        },
        "inLanguage": "zh-CN"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryJsonLd) }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <nav className="px-4 md:px-6 py-4 max-w-7xl mx-auto" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to="/category" className="hover:text-primary-600 transition-colors cursor-pointer">分类</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-900 font-medium">{category.name}</li>
          </ol>
        </nav>

        <section className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[65%]">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500/10 text-primary-600 rounded-xl">
                    <i className={`${category.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900">
                      {category.name}
                    </h1>
                    <p className="text-foreground-600 text-sm mt-0.5">{category.description}</p>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  {categoryArticles.length > 0 ? (
                    categoryArticles.map((article, idx) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className={`group flex flex-col sm:flex-row gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 hover:-translate-y-0.5 ${
                          idx === 0 ? 'bg-primary-50' : 'bg-background-100 hover:bg-background-200/70'
                        }`}
                      >
                        <div className={`relative rounded-xl overflow-hidden flex-shrink-0 ${
                          idx === 0 ? 'w-full sm:w-[45%] aspect-video' : 'w-full sm:w-40 aspect-[4/3]'
                        }`}>
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                          {article.isExclusive && (
                            <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                              独家
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                              {article.category}
                            </span>
                            <span className="flex items-center gap-1 text-foreground-500 text-xs">
                              <i className="ri-fire-line text-accent-500 text-xs"></i>
                              {article.hotScore >= 10000
                                ? `${(article.hotScore / 10000).toFixed(1)}万`
                                : article.hotScore}
                            </span>
                          </div>
                          <h3 className={`font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 ${
                            idx === 0 ? 'text-lg md:text-xl' : 'text-base'
                          }`}>
                            {article.title}
                          </h3>
                          <p className={`text-foreground-600 line-clamp-2 mt-1.5 ${
                            idx === 0 ? 'text-sm' : 'text-xs'
                          }`}>
                            {article.excerpt}
                          </p>
                          <p className="mt-2 text-foreground-500 text-xs">
                            {article.date} · {article.author}
                          </p>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="text-center py-20 text-foreground-500">
                      <i className="ri-inbox-line text-4xl mb-3 block"></i>
                      <p>该分类暂无内容</p>
                    </div>
                  )}
                </div>
              </div>

              <aside className="lg:w-[35%]">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-100 rounded-2xl p-5">
                    <h3 className="font-heading font-bold text-lg text-foreground-900 mb-4">分类导航</h3>
                    <div className="flex flex-col gap-2">
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                            cat.slug === slug
                              ? 'bg-primary-500 text-background-50'
                              : 'text-foreground-700 hover:bg-background-200/70'
                          }`}
                        >
                          <i className={cat.icon}></i>
                          {cat.name}
                          <span className="ml-auto text-xs opacity-70">{cat.articleCount}</span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-6 text-background-50">
                    <h3 className="font-heading font-bold text-lg mb-2">下载51吃瓜App</h3>
                    <p className="text-sm text-background-50/80 mb-4">
                      随时随地畅快吃瓜，爆料推送快人一步
                    </p>
                    <a
                      href="#download"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 text-primary-600 font-semibold text-sm rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-2-line"></i>
                      立即下载
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}